// Página "Mi perfil": cada entrenador/coordinador edita sus propios datos,
// cambia su contraseña y activa/desactiva los avisos push desde aquí.
import { useEffect, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Skeleton from "@mui/material/Skeleton";

import PersonIcon from "@mui/icons-material/Person";
import SaveIcon from "@mui/icons-material/Save";
import LockIcon from "@mui/icons-material/Lock";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import NotificationsIcon from "@mui/icons-material/Notifications";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import apiFetch from "services/http";
import { PageToolbar, useNotify } from "components/Convokko";
import {
  pushSoportado,
  obtenerSuscripcionActual,
  activarAvisos,
  desactivarAvisos,
} from "utils/push";

const ROL_LABELS = {
  coordinador: "Coordinador",
  entrenador: "Entrenador",
};

const sectionCardSx = {
  borderRadius: 3,
  border: "1px solid",
  borderColor: "grey.200",
  p: { xs: 2.5, sm: 3 },
  height: "100%",
};

const datosVacios = { nombre: "", email: "", dni: "", fecha_nacimiento: "" };
const passwordsVacios = { actual: "", nueva: "", confirmar: "" };

export default function Perfil() {
  const notify = useNotify();

  const [loading, setLoading] = useState(true);
  const [perfil, setPerfil] = useState(null);
  const [datos, setDatos] = useState(datosVacios);
  const [guardandoDatos, setGuardandoDatos] = useState(false);

  const [passwords, setPasswords] = useState(passwordsVacios);
  const [mostrarPassword, setMostrarPassword] = useState(false);
  const [guardandoPassword, setGuardandoPassword] = useState(false);

  const [pushActivo, setPushActivo] = useState(null); // null = comprobando
  const [pushCargando, setPushCargando] = useState(false);

  const cargarPerfil = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiFetch("/api/entrenadores/mi-perfil");
      setPerfil(data);
      setDatos({
        nombre: data.nombre || "",
        email: data.email || "",
        dni: data.dni || "",
        fecha_nacimiento: data.fecha_nacimiento || "",
      });
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    cargarPerfil();
  }, [cargarPerfil]);

  useEffect(() => {
    if (!pushSoportado()) {
      setPushActivo(false);
      return;
    }
    obtenerSuscripcionActual()
      .then((sub) => setPushActivo(Boolean(sub)))
      .catch(() => setPushActivo(false));
  }, []);

  const handleChangeDatos = (e) => {
    const { name, value } = e.target;
    setDatos((prev) => ({ ...prev, [name]: value }));
  };

  const handleGuardarDatos = async () => {
    try {
      setGuardandoDatos(true);
      await apiFetch("/api/entrenadores/mi-perfil", {
        method: "PUT",
        body: {
          nombre: datos.nombre,
          email: datos.email,
          dni: datos.dni || null,
          fecha_nacimiento: datos.fecha_nacimiento || null,
        },
      });
      notify.success("Perfil actualizado");
      await cargarPerfil();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setGuardandoDatos(false);
    }
  };

  const handleChangePassword = (e) => {
    const { name, value } = e.target;
    setPasswords((prev) => ({ ...prev, [name]: value }));
  };

  const handleGuardarPassword = async () => {
    if (passwords.nueva !== passwords.confirmar) {
      notify.error("Las contraseñas nuevas no coinciden");
      return;
    }
    try {
      setGuardandoPassword(true);
      await apiFetch("/api/entrenadores/mi-perfil/password", {
        method: "PUT",
        body: { password_actual: passwords.actual, password_nueva: passwords.nueva },
      });
      notify.success("Contraseña actualizada");
      setPasswords(passwordsVacios);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setGuardandoPassword(false);
    }
  };

  const handleTogglePush = async () => {
    setPushCargando(true);
    try {
      if (pushActivo) {
        await desactivarAvisos();
        setPushActivo(false);
        notify.info("Avisos desactivados en este dispositivo.");
      } else {
        await activarAvisos();
        setPushActivo(true);
        notify.success(
          "Avisos activados: te avisaremos si se acerca un partido sin convocatoria y cuando vaya a empezar un entrenamiento para pasar asistencia."
        );
        apiFetch("/api/push/probar", { method: "POST" }).catch(() => {});
      }
    } catch (err) {
      notify.error(err.message || "No se pudieron activar los avisos.");
    } finally {
      setPushCargando(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box py={3}>
        <PageToolbar icon={PersonIcon} title="Mi perfil" showSeason={false} />

        {loading ? (
          <Grid container spacing={3}>
            {[0, 1, 2].map((i) => (
              <Grid item xs={12} md={i === 2 ? 12 : 6} key={i}>
                <Card sx={sectionCardSx}>
                  <Skeleton variant="text" width="40%" height={28} />
                  <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2, mt: 2 }} />
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card sx={sectionCardSx}>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  <Typography variant="h6" fontWeight={700}>
                    Datos personales
                  </Typography>
                  <Chip
                    label={ROL_LABELS[perfil?.rol] || "Usuario"}
                    size="small"
                    color="secondary"
                    sx={{ fontWeight: 700, height: 20, fontSize: "0.68rem" }}
                  />
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" mb={2.5}>
                  {[perfil?.nombre_equipo_actual, perfil?.nombre_escuela]
                    .filter(Boolean)
                    .join(" · ") || "Sin equipo asignado"}
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    label="Nombre completo"
                    name="nombre"
                    value={datos.nombre}
                    onChange={handleChangeDatos}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    value={datos.email}
                    onChange={handleChangeDatos}
                    fullWidth
                    required
                  />
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="DNI"
                        name="dni"
                        value={datos.dni}
                        onChange={handleChangeDatos}
                        fullWidth
                        placeholder="12345678A"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Fecha de nacimiento"
                        name="fecha_nacimiento"
                        type="date"
                        value={datos.fecha_nacimiento}
                        onChange={handleChangeDatos}
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                  </Grid>
                  <Box textAlign="right">
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleGuardarDatos}
                      disabled={guardandoDatos || !datos.nombre || !datos.email}
                      sx={{ borderRadius: 2 }}
                    >
                      {guardandoDatos ? "Guardando..." : "Guardar cambios"}
                    </Button>
                  </Box>
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card sx={sectionCardSx}>
                <Stack direction="row" alignItems="center" spacing={1} mb={2.5}>
                  <LockIcon fontSize="small" color="action" />
                  <Typography variant="h6" fontWeight={700}>
                    Cambiar contraseña
                  </Typography>
                </Stack>
                <Stack spacing={2}>
                  <TextField
                    label="Contraseña actual"
                    name="actual"
                    type={mostrarPassword ? "text" : "password"}
                    value={passwords.actual}
                    onChange={handleChangePassword}
                    fullWidth
                  />
                  <TextField
                    label="Nueva contraseña"
                    name="nueva"
                    type={mostrarPassword ? "text" : "password"}
                    value={passwords.nueva}
                    onChange={handleChangePassword}
                    fullWidth
                    helperText="Mínimo 6 caracteres"
                  />
                  <TextField
                    label="Repetir nueva contraseña"
                    name="confirmar"
                    type={mostrarPassword ? "text" : "password"}
                    value={passwords.confirmar}
                    onChange={handleChangePassword}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setMostrarPassword((v) => !v)}
                            edge="end"
                            size="small"
                          >
                            {mostrarPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box textAlign="right">
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleGuardarPassword}
                      disabled={
                        guardandoPassword ||
                        !passwords.actual ||
                        !passwords.nueva ||
                        !passwords.confirmar
                      }
                      sx={{ borderRadius: 2 }}
                    >
                      {guardandoPassword ? "Guardando..." : "Actualizar contraseña"}
                    </Button>
                  </Box>
                </Stack>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card sx={sectionCardSx}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={2}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <NotificationsIcon color="action" />
                    <Box>
                      <Typography variant="h6" fontWeight={700}>
                        Avisos
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Recibe una notificación en este dispositivo si se acerca un partido sin
                        convocatoria, y cuando vaya a empezar un entrenamiento de tu equipo para
                        pasar asistencia.
                      </Typography>
                    </Box>
                  </Stack>

                  {pushSoportado() ? (
                    <Button
                      variant="outlined"
                      startIcon={
                        pushCargando ? undefined : pushActivo ? (
                          <NotificationsActiveIcon />
                        ) : (
                          <NotificationsOffIcon />
                        )
                      }
                      onClick={handleTogglePush}
                      disabled={pushCargando || pushActivo === null}
                      sx={{
                        borderRadius: 2.5,
                        borderColor: pushActivo ? "success.main" : "grey.400",
                        color: pushActivo ? "success.main" : "text.secondary",
                        whiteSpace: "nowrap",
                        "&:hover": { borderColor: "success.main", color: "success.main" },
                      }}
                    >
                      {pushActivo ? "Avisos activados" : "Activar avisos"}
                    </Button>
                  ) : (
                    <Chip label="No disponible en este navegador" size="small" variant="outlined" />
                  )}
                </Stack>
              </Card>
            </Grid>
          </Grid>
        )}
      </Box>
      <Footer />
    </DashboardLayout>
  );
}
